import { consola } from 'consola'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'

/**
 * Утилита для определения платформы и возможностей приложения
 * Следует принципу Single Responsibility (SOLID)
 */

/**
 * Проверяет, работает ли приложение на нативной платформе (iOS/Android)
 */
export const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform()
}

/**
 * Проверяет, работает ли приложение в веб-версии
 */
export const isWebPlatform = (): boolean => {
  return Capacitor.getPlatform() === 'web'
}

/**
 * Проверяет, работает ли приложение на iOS
 */
export const isIOS = (): boolean => {
  return Capacitor.getPlatform() === 'ios'
}

/**
 * Проверяет, работает ли приложение на Android
 */
export const isAndroid = (): boolean => {
  return Capacitor.getPlatform() === 'android'
}

/**
 * Получает название текущей платформы
 */
export const getPlatform = (): 'web' | 'ios' | 'android' => {
  return Capacitor.getPlatform() as 'web' | 'ios' | 'android'
}

/**
 * Проверяет, является ли устройство мобильным (через user agent)
 * Используется для веб-версии
 */
export const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false
  
  const userAgent = navigator.userAgent || navigator.vendor
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
  
  return mobileRegex.test(userAgent.toLowerCase())
}

/**
 * Проверяет, является ли браузер Safari
 */
export const isSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false
  
  const userAgent = navigator.userAgent.toLowerCase()
  return userAgent.includes('safari') && !userAgent.includes('chrome')
}

/**
 * Получает информацию о текущей платформе
 */
export const getPlatformInfo = () => {
  return {
    platform: getPlatform(),
    isNative: isNativePlatform(),
    isWeb: isWebPlatform(),
    isIOS: isIOS(),
    isAndroid: isAndroid(),
    isMobile: isMobileDevice(),
    isSafari: isSafari(),
  }
}

/**
 * Определяет, нужно ли использовать Capacitor Browser для OAuth
 * (на нативных платформах используем Capacitor Browser, на вебе - window.open)
 */
export const shouldUseCapacitorBrowser = (): boolean => {
  return isNativePlatform()
}

/**
 * Открывает URL через Capacitor Browser на нативных платформах
 * или через window.open на вебе
 */
export const openExternalUrl = async (url: string): Promise<{ success: boolean; method: 'capacitor' | 'window' | 'none' }> => {
  if (shouldUseCapacitorBrowser()) {
    try {
      await Browser.open({ url })
      return { success: true, method: 'capacitor' }
    } catch (error) {
      consola.error('Error opening Capacitor Browser:', error)
      // Fallback to window.open if Capacitor Browser fails
      const newWindow = window.open(url, '_blank')
      return { 
        success: !!newWindow && !newWindow.closed, 
        method: newWindow ? 'window' : 'none', 
      }
    }
  } else {
    // Web platform
    const newWindow = window.open(url, '_blank')
    return { 
      success: !!newWindow && !newWindow.closed, 
      method: newWindow ? 'window' : 'none', 
    }
  }
}

/**
 * Закрывает Capacitor Browser на нативных платформах
 * Используется для закрытия браузера после успешной авторизации
 */
export const closeCapacitorBrowser = async (): Promise<void> => {
  if (isNativePlatform()) {
    try {
      await Browser.close()
    } catch (error) {
      consola.warn('Error closing Capacitor Browser:', error)
    }
  }
}

